import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import BloqueFicha from "../components/funcionales/BloqueFicha";
import {
  corridasRecientes,
  genteParaCompartir,
  listarAutomatizaciones,
  miMail,
  misPlanillas,
} from "../services/automatizaciones";
import {
  estadoCorrida,
  mapaDeGente,
  resumenDisparador,
  resumenPaso,
  ultimaPorAutomatizacion,
} from "../shared/automatizaciones";
import { diaHoraAr } from "../shared/fechaAr";

import "./css/fichas-pantalla.css";
import "./css/automatizaciones.css";

/* Automatizaciones: la lista. TODAS, de todos.
 *
 * Igna, 03/10/2026: «un área para automatizar flujos, en estilo programación
 * por bloques, que todos puedan verlo». Que todos vean las de todos no es un
 * detalle: el que quiere armar la suya mira cómo la armó otro, y el día que un
 * WhatsApp raro le llega a alguien, cualquiera puede ver de dónde salió. Tocar,
 * sólo el dueño (lo exige la base; acá sólo se esconden los botones).
 *
 * Cada tarjeta dice lo que hace falta para no abrirla: de quién es, si está
 * prendida, cuándo corre (en castellano, no el jsonb) y cómo le fue la última
 * vez. Lo de «cómo le fue» es lo que más se mira: una automatización apagada
 * no preocupa; una prendida que da error todos los viernes, sí.
 *
 * «Cómo le fue» sí es de todos; QUÉ leyó y QUÉ mandó, no: el detalle de una
 * corrida trae valores de las planillas y los mails de quien la armó, y lo ve
 * sólo él (`20261005060000_automatizaciones_valores_privados.sql`). La última
 * corrida sale de la vista pública, que tiene las de todos sin el detalle.
 *
 * Lo secundario —los nombres de la gente, la última corrida, los nombres de
 * las planillas— se trae aparte y si falla no tumba la lista: se ve el mail en
 * vez del nombre, o «sin datos de corridas», pero las automatizaciones se ven.
 */

const FILTROS = [
  { clave: "todas", label: "Todas" },
  { clave: "mias", label: "Mías" },
];

function UltimaCorrida({ corrida, sinDatos }) {
  if (sinDatos) return <span className="tenue">No se pudo consultar</span>;
  if (!corrida) return <span className="tenue">Nunca corrió</span>;
  const e = estadoCorrida(corrida.estado);
  return (
    <span className="auto-ultima">
      <span className={`auto-estado auto-estado--${e.tono}`}>{e.label}</span>
      <span className="tenue">
        {diaHoraAr(corrida.terminada_en || corrida.pedida_en)}
        {corrida.tipo === "prueba" ? " · prueba" : ""}
      </span>
    </span>
  );
}

const Automatizaciones = () => {
  const [estado, setEstado] = useState("cargando");
  const [autos, setAutos] = useState([]);
  const [yo, setYo] = useState("");
  const [gente, setGente] = useState({});
  const [planillas, setPlanillas] = useState({});
  const [ultimas, setUltimas] = useState({});
  const [sinCorridas, setSinCorridas] = useState(false);
  const [filtro, setFiltro] = useState("todas");

  useEffect(() => {
    let vivo = true;
    Promise.all([listarAutomatizaciones(), miMail()])
      .then(([lista, mail]) => {
        if (!vivo) return;
        setAutos(lista);
        setYo((mail || "").toLowerCase());
        setEstado("listo");
        corridasRecientes(lista.map((a) => a.id))
          .then((cs) => vivo && setUltimas(ultimaPorAutomatizacion(cs)))
          .catch(() => vivo && setSinCorridas(true));
      })
      .catch(() => vivo && setEstado("error"));
    genteParaCompartir()
      .then((g) => vivo && setGente(mapaDeGente(g)))
      .catch(() => {});
    misPlanillas()
      .then((ps) => vivo && setPlanillas(Object.fromEntries(ps.map((p) => [p.id, p.nombre]))))
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const visibles = useMemo(
    () => (filtro === "mias" ? autos.filter((a) => (a.dueno || "").toLowerCase() === yo) : autos),
    [autos, filtro, yo],
  );

  const vacio = estado === "listo" && visibles.length === 0
    ? (filtro === "mias"
      ? "Todavía no armaste ninguna. Tocá «Nueva automatización» para empezar: arranca apagada, la probás y después la prendés."
      : "Todavía no hay ninguna automatización. Armá la primera con «Nueva automatización».")
    : null;

  return (
    <div className="docs auto">
      <div className="auto-barra">
        <div className="tk-filtros auto-filtros" role="group" aria-label="Filtrar">
          {FILTROS.map((f) => (
            <button
              key={f.clave} type="button" aria-pressed={filtro === f.clave}
              className={"tk-chip" + (filtro === f.clave ? " activo" : "")}
              onClick={() => setFiltro(f.clave)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Link to="/automatizaciones/nueva" className="btn-primario auto-nueva">+ Nueva automatización</Link>
      </div>

      <BloqueFicha
        titulo={estado === "listo" ? `Automatizaciones · ${visibles.length}` : "Automatizaciones"}
        estado={estado}
        vacio={vacio}
      >
        <ul className="auto-lista">
          {visibles.map((a) => {
            const dueno = (a.dueno || "").toLowerCase();
            const esMia = dueno === yo;
            const pasos = Array.isArray(a.pasos) ? a.pasos : [];
            return (
              <li key={a.id}>
                <Link to={`/automatizaciones/${a.id}`} className="auto-card" data-testid="auto-card">
                  <div className="auto-card-cab">
                    <span className="auto-card-nombre">{a.nombre}</span>
                    <span className={"auto-onoff" + (a.activa ? " auto-onoff--on" : "")}>
                      {a.activa ? "Prendida" : "Apagada"}
                    </span>
                  </div>
                  <div className="auto-card-dueno tenue">
                    {esMia ? "Tuya" : `De ${gente[dueno] || dueno || "alguien"}`}
                  </div>
                  <div className="auto-card-cuando">{resumenDisparador(a.disparador, planillas)}</div>
                  {pasos.length > 0 && (
                    <div className="auto-card-pasos tenue">
                      → {pasos.map((p) => resumenPaso(p, gente)).join(" · ")}
                    </div>
                  )}
                  <div className="auto-card-pie">
                    <UltimaCorrida corrida={ultimas[a.id]} sinDatos={sinCorridas} />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </BloqueFicha>

    </div>
  );
};

export default Automatizaciones;
